import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import Script from 'next/script';
import { v4 as uuidv4 } from 'uuid';
import { useLocale } from '@hooks/useLocale';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer/index';

function createSessionId() {
  const sessionId = Cookies.get('sessionId') || uuidv4();
  Cookies.set('sessionId', sessionId);
  return sessionId;
}

// OneTrust only auto-activates optanon-category-tagged scripts present in the DOM
// at its own load-time scan; this script is inserted later via next/script
// (afterInteractive), so it's never caught by that scan or by consent-change
// reprocessing (this repo's OptanonWrapper is a no-op). Gate manually instead,
// matching the pattern in app/[locale]/ThirdPartyScripts.tsx.
function hasAlgonomyConsent() {
  return (
    typeof window !== 'undefined' &&
    !!window.OnetrustActiveGroups?.includes('C0003')
  );
}

function isOnetrustResolved() {
  return typeof window !== 'undefined' && !!window.OnetrustActiveGroups;
}

function formatPlacementData(placements: AlgonomyPlacement[]) {
  placements.forEach((placement) => {
    // So our text was coming back from the new p13n.js with text like "Today&rsquo;s Regional Hot Products"
    // Clearly, we had some html encoded special characters there. Use this 1 simple trick to rid ourselves of them...
    // Inspired by https://stackoverflow.com/questions/7394748/whats-the-right-way-to-decode-a-string-that-has-special-html-entities-in-it
    const tempTextElement = document.createElement('textarea');
    tempTextElement.innerHTML = placement.message;
    placement.message = tempTextElement.value;
  });
  return placements;
}

export interface AlgonomyProduct {
  id: string;
  detailURL: string;
  linkURL: string;
  imageURL: string;
  displayName: string;
  rating: string;
  reviews: string;
  productUrl: string;
  displayPrice: string;
  displaySalePrice: string;
  saleprice: string;
  trackingimpressionCategoryPath: string;
  trackingimpressionDimension5: string;
  brand: string;
  gender: string;
  variantColour: string;
}

export interface AlgonomyPlacement {
  items: AlgonomyProduct[];
  message: string;
  name: string;
  strategy: string;
}

interface RRLoaderProps {
  onLoad?: (data: AlgonomyPlacement[]) => void | Promise<void>;
  pageType: string;
  data?: any;
  trigger: boolean;
}

export const PRODUCT_MINI_SINGLE = 'item_page.product_min_card';
export const PRODUCT_MINI_CAROUSEL = 'item_page.product_min_carousel';
export const PDP_OUTFIT_PLACEMENT = 'item_page.product_outfitting';

export const placementTypes = {
  // pulled from https://github.com/mountainequipmentcoop/hybris/blob/develop/mecfacades/project.properties
  home: ['home_page.home1_rr', 'home_page.home3_rr'],
  pdp: {
    inStock: [
      PRODUCT_MINI_CAROUSEL,
      PRODUCT_MINI_SINGLE,
      PDP_OUTFIT_PLACEMENT,
      'item_page.product1_rr',
      'item_page.product2_rr',
      'item_page.product3_rr',
      'item_page.collection1_rr',
    ],
    outOfStock: ['item_page.product0_rr'],
  },
  cart: {
    default: ['cart_page.cart2_rr'],
    added: ['add_to_cart_page.addtocart1_rr'],
  },
  plp: [
    'category_page.category1_rr',
    'category_page.category2_rr',
    'category_page.category3_rr',
  ],
  brand: [
    'brand_page.brandcollection1_rr',
    'brand_page.brandcollection2_rr',
    'brand_page.brandbestsellers_rr',
    'brand_page.category1_rr',
  ],
  categoryLandingPage: [
    'category_page.intro1_rr',
    'category_page.intro2_rr',
    'category_page.intro3_rr',
  ],
  search: ['search_page'],
  landingPage: [
    'category_page.landing_page_rr',
    'category_page.landing_page1_rr',
    'category_page.landing_page2_rr',
    'category_page.landing_page3_rr',
  ],
};

const resetR3Params = () => {
  if (typeof window.R3_ITEM_INSTANCE !== 'undefined')
    window.R3_ITEM_INSTANCE = undefined;

  if (typeof window.R3_COMMON_INSTANCE !== 'undefined')
    window.R3_COMMON_INSTANCE.placementTypes = '';

  if (
    window.R3_COMMON_INSTANCE?.categoryHintIds &&
    window.R3_COMMON_INSTANCE.categoryHintIds?.length
  )
    window.R3_COMMON_INSTANCE.categoryHintIds = '';
};

export const RRTrackAddToCart = ({
  itemId,
  skuId,
  categoryId,
}: {
  itemId: string;
  skuId: string;
  categoryId?: string;
}) => {
  if (!window.R3_COMMON_INSTANCE || !window.r3_addtocart) return;

  resetR3Params();

  window.R3_ADDTOCART_INSTANCE = new window.r3_addtocart();
  window.R3_ADDTOCART_INSTANCE.addItemIdToCart(itemId, skuId);

  if (categoryId) window.R3_COMMON_INSTANCE.addCategoryHintId(categoryId);
};

function runLoader(
  triggerR3: boolean,
  currentUserId: number | undefined,
  pageType: string,
  locale: string | undefined,
  onLoad?: (data: AlgonomyPlacement[]) => void | Promise<void>,
  data?: any
) {
  if (triggerR3) {
    resetR3Params();
    window.R3_COMMON_INSTANCE = new window.r3_common();
    window.R3_COMMON_INSTANCE.setApiKey(
      process.env.NEXT_PUBLIC_ALGONOMY_API_KEY
    );
    window.R3_COMMON_INSTANCE.setBaseUrl(
      `${window.location.protocol}${process.env.NEXT_PUBLIC_ALGONOMY_BASE_URL}`
    );

    // This is to allow recommendations to work on preview environments
    const baseUrl = window.location.host.includes(
      'mountainequipment.vercel.app'
    )
      ? `${window.location.protocol}//${'bc-test.mec.ca'}`
      : `${window.location.protocol}//${window.location.host}`;

    window.R3_COMMON_INSTANCE.setClickthruServer(baseUrl);
    window.R3_COMMON_INSTANCE.setRegionId(locale);
    window.R3_COMMON_INSTANCE.setSessionId(createSessionId());
    if (currentUserId) {
      window.R3_COMMON_INSTANCE.setUserId(currentUserId);
    }
    if (pageType === 'search') {
      window.R3_SEARCH_INSTANCE = new window.r3_search();

      placementTypes.search.forEach((type) => {
        window.R3_COMMON_INSTANCE.addPlacementType(type);
      });

      if (data.searchTerm) {
        window.R3_SEARCH_INSTANCE.setTerms(data.searchTerm);
      }
      if (data.itemIds?.length) {
        data.itemIds.forEach((productId: string) =>
          window.R3_SEARCH_INSTANCE.addItemId(productId)
        );
      }
    } else if (pageType === 'collection' || pageType === 'brand') {
      window.R3_CATEGORY_INSTANCE = new window.r3_category();

      if (data.categoryId) window.R3_CATEGORY_INSTANCE.setId(data.categoryId);

      if (data.brandName)
        window.R3_COMMON_INSTANCE.setPageBrand(data.brandName);

      const placements =
        pageType === 'collection' ? placementTypes.plp : placementTypes.brand;

      placements.forEach((type) => {
        window.R3_COMMON_INSTANCE.addPlacementType(type);
      });

      if (data.itemIds?.length) {
        data.itemIds.forEach((productId: string) =>
          window.R3_CATEGORY_INSTANCE.addItemId(productId)
        );
      }
    } else if (pageType === 'pdp') {
      window.R3_ITEM_INSTANCE = new window.r3_item();
      window.R3_ITEM_INSTANCE.setId(data?.productCode);
      window.R3_ITEM_INSTANCE.setName(data?.productName);
      const placements = data?.isInStock
        ? placementTypes.pdp.inStock
        : placementTypes.pdp.outOfStock;
      placements.forEach((type) => {
        window.R3_COMMON_INSTANCE.addPlacementType(type);
      });
    } else if (pageType === 'home') {
      window.R3_HOME_INSTANCE = new window.r3_home();
      placementTypes.home.forEach((type) => {
        window.R3_COMMON_INSTANCE.addPlacementType(type);
      });
    } else if (pageType === 'cart') {
      window.R3_CART_INSTANCE = new window.r3_cart();

      window.R3_COMMON_INSTANCE.addPlacementType(placementTypes.cart.default);
      if (data?.cartData) {
        data?.cartData?.line_items?.physical_items.forEach((item: any) => {
          const itemCode = item.code;
          const itemSku = item.sku;
          window.R3_CART_INSTANCE.addItemId(itemCode, itemSku);
        });
      }
    } else if (pageType === 'cart-added') {
      placementTypes.cart.added.forEach((type) => {
        window.R3_COMMON_INSTANCE.addPlacementType(type);
      });
    } else if (pageType === 'landingPage') {
      window.R3_CATEGORY_INSTANCE = new window.r3_category();

      if (data.category) window.R3_CATEGORY_INSTANCE.setId(data.category);

      placementTypes.landingPage.forEach((type) => {
        window.R3_COMMON_INSTANCE.addPlacementType(type);
      });
    } else if (pageType === 'categoryLandingPage') {
      window.R3_CATEGORY_INSTANCE = new window.r3_category();

      if (data.category) window.R3_CATEGORY_INSTANCE.setId(data.category);

      placementTypes.categoryLandingPage.forEach((type) => {
        window.R3_COMMON_INSTANCE.addPlacementType(type);
      });
    }
    window.RR.jsonCallback = function () {
      onLoad?.(formatPlacementData(window.RR.data.JSON.placements));
    };
    window.rr_flush_onload();

    window.r3();
  }

  return true;
}

export function Loader({
  onLoad,
  pageType,
  data = {},
  trigger,
}: RRLoaderProps) {
  const [isRichRelevanceLoaded, setLoaded] = useState(
    typeof window !== 'undefined' && typeof window.RR !== 'undefined'
  );
  const [hasConsent, setHasConsent] = useState(hasAlgonomyConsent());
  const { customer } = useCustomer();
  const locale = useLocale();
  /**
   *
   * Product Page: https://help.richrelevance.com/Integration_Guide/0010_JSON_Integration%3A_Core_Integration/0013_JSON_Integration%3A_Item_Page
   * Category Page: https://help.richrelevance.com/Integration_Guide/0010_JSON_Integration%3A_Core_Integration/0012_JSON_Integration%3A_Category_Page
   *
   * Links to all options: https://help.richrelevance.com/Integration_Guide
   */
  const [cache, setCache] = useState<{
    data?: string;
    entityId?: number;
    isRichRelevanceLoaded?: boolean;
    pageType?: string;
    onLoad?: string;
    locale?: string;
  }>({});

  useEffect(() => {
    if (isRichRelevanceLoaded) {
      if (
        (cache?.data !== JSON.stringify(data) ||
          cache?.entityId !== customer?.entityId ||
          cache?.pageType !== pageType ||
          cache?.onLoad !== JSON.stringify(onLoad) ||
          cache?.locale !== locale) &&
        trigger
      ) {
        setCache({
          data: JSON.stringify(data),
          entityId: customer?.entityId,
          pageType,
          onLoad: JSON.stringify(onLoad),
          locale,
        });

        Promise.resolve(
          runLoader(trigger, customer?.entityId, pageType, locale, onLoad, data)
        );
      }
    }
  }, [
    data,
    isRichRelevanceLoaded,
    pageType,
    onLoad,
    locale,
    cache?.data,
    cache?.entityId,
    cache.isRichRelevanceLoaded,
    cache?.pageType,
    cache?.onLoad,
    cache?.locale,
    trigger,
    customer?.entityId,
  ]);

  useEffect(() => {
    if (!hasConsent || isRichRelevanceLoaded) return undefined;
    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && window.RR !== undefined) {
        clearInterval(interval);
        setLoaded(true);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [hasConsent, isRichRelevanceLoaded]);

  useEffect(() => {
    if (hasConsent) return undefined;
    // OnetrustActiveGroups is often still an empty string on this first check
    // (OneTrust resolves stored consent asynchronously and doesn't fire
    // OneTrustGroupsUpdated for that initial resolution, only for later
    // interactive changes), so poll it the same way the effect above polls for
    // window.RR. Once OnetrustActiveGroups is a non-empty string (resolved,
    // with or without our group present), stop polling and rely on
    // OneTrustGroupsUpdated alone for any later opt-in.
    const checkConsent = () => {
      if (hasAlgonomyConsent()) {
        setHasConsent(true);
      } else if (isOnetrustResolved()) {
        clearInterval(interval);
      }
    };
    const interval = setInterval(checkConsent, 300);
    window.addEventListener('OneTrustGroupsUpdated', checkConsent);
    return () => {
      clearInterval(interval);
      window.removeEventListener('OneTrustGroupsUpdated', checkConsent);
    };
  }, [hasConsent]);

  if (!isRichRelevanceLoaded) {
    if (!hasConsent) return null;
    return (
      <Script
        src="//media.algorecs.com/rrserver/js/2.1/p13n.js"
        onLoad={() =>
          setLoaded(
            typeof window !== 'undefined' && typeof window.RR !== 'undefined'
          )
        }
      />
    );
  }

  return null;
}

export default Loader;
